import { ActionType } from '@unifyapps/defs/types/action';
import type { OnActionType } from '../../../context/ActionsProvider/context';
import type { PageEventPayload } from '../../useGlobalBlockActions/actions/emitPageEvent/useEmitPageEvent';
import type { LogToDebuggerPayload } from '../../useGlobalBlockActions/actions/logToDebugger/useLogToDebugger';
import type {
  NavigateToPagePayload,
  SetURLParametersPayload,
} from '../../useGlobalBlockActions/actions/navigateToPage/useNavigateToPage';
import type { ShowNotificationPayload } from '../../useGlobalBlockActions/actions/showNotification/useShowNotification';

export type ContextActionScope = 'page' | 'app';

type ContextActionDefinition = {
  scopes: ReadonlySet<ContextActionScope>;
  actionType: ActionType;
};

const PAGE_SCOPES: ReadonlySet<ContextActionScope> = new Set(['page']);
const PAGE_AND_APP_SCOPES: ReadonlySet<ContextActionScope> = new Set(['page', 'app']);

const CONTEXT_ACTIONS = {
  emitPageEvent: {
    scopes: PAGE_SCOPES,
    actionType: ActionType.EmitPageEvent,
  },
  showNotification: {
    scopes: PAGE_AND_APP_SCOPES,
    actionType: ActionType.ShowNotification,
  },
  logToDebugger: {
    scopes: PAGE_AND_APP_SCOPES,
    actionType: ActionType.LogToDebugger,
  },
  // Page scope only: both are dispatched by usePageActions, and the app-level
  // provider above it has no case for them — an app-scope entry would no-op.
  navigateToPage: {
    scopes: PAGE_SCOPES,
    actionType: ActionType.NavigateToPage,
  },
  setURLParameters: {
    scopes: PAGE_SCOPES,
    actionType: ActionType.SetURLParameters,
  },
} as const satisfies Record<string, ContextActionDefinition>;

export type ContextActions = {
  emitPageEvent?: (payload: PageEventPayload) => void;
  showNotification?: (payload: ShowNotificationPayload) => void;
  logToDebugger?: (payload: LogToDebuggerPayload) => void;
  navigateToPage?: (payload: NavigateToPagePayload) => void;
  setURLParameters?: (payload: SetURLParametersPayload) => void;
};

const isMissingOnActionLogged: Record<string, boolean> = {};

const buildAction =
  <TPayload>({
    actionType,
    onAction,
    instanceId,
  }: {
    actionType: ActionType;
    onAction?: OnActionType;
    instanceId?: string;
  }) =>
  (payload: TPayload) => {
    if (!onAction) {
      if (!isMissingOnActionLogged[actionType]) {
        console.warn(
          `[computeContext] ${actionType} dispatched without onAction; check ActionsProvider mount`,
        );
        isMissingOnActionLogged[actionType] = true;
      }
      return;
    }
    onAction({
      action: { actionType, payload: payload as Record<string, unknown> },
      actionContext: {},
      instanceId,
    }).catch((err) => {
      console.debug(`[computeContext] ${actionType} dispatch failed`, err);
    });
  };

export const getContextActions = ({
  onAction,
  instanceId,
  scope,
}: {
  onAction?: OnActionType;
  instanceId?: string;
  scope: ContextActionScope;
}): ContextActions => {
  const bag: Record<string, (payload: unknown) => void> = {};
  for (const [actionName, definition] of Object.entries(CONTEXT_ACTIONS)) {
    if (definition.scopes.has(scope)) {
      bag[actionName] = buildAction({ actionType: definition.actionType, onAction, instanceId });
    }
  }
  return bag as ContextActions;
};
